import { useTranslation } from "react-i18next";import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { usePermissions } from '@/shared/contexts/PermissionContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Shield, Lock } from 'lucide-react';

/**
 * PermissionGate - Conditionally renders children based on permission
 * 
 * Usage:
 *   <PermissionGate permission="wms.inventory.edit">
 *     <Button>Edit Inventory</Button>
 *   </PermissionGate>
 * 
 *   <PermissionGate permissions={["wms.inventory.edit", "wms.inventory.adjust"]} requireAll={false}>
 *     <Button>Modify Stock</Button>
 *   </PermissionGate>
 */
export const PermissionGate = ({
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  children
}) => {const { t } = useTranslation("common");
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ?
    hasAllPermissions(permissions) :
    hasAnyPermission(permissions);
  } else {
    hasAccess = true; // No permission specified = show
  }

  if (!hasAccess) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * ProtectedRoute - Route wrapper that checks permissions
 * 
 * Usage:
 *   <Route path="/admin/users" element={
 *     <ProtectedRoute permission="admin.users.view">
 *       <UsersPage />
 *     </ProtectedRoute>
 *   } />
 */
export const ProtectedRoute = ({
  permission,
  permissions = [],
  requireAll = false,
  redirectTo = '/access-denied',
  children
}) => {const { t } = useTranslation("common");
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth/permissions
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>);

  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ?
    hasAllPermissions(permissions) :
    hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} state={{ from: location, requiredPermission: permission || permissions }} replace />;
  }

  return <>{children}</>;
};

/**
 * AccessDeniedPage - Display when user doesn't have permission
 */
export const AccessDeniedPage = () => {const { t } = useTranslation("common");
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { getDefaultAllowedRoute, loading: permissionsLoading } = usePermissions();
  const requiredPermission = location.state?.requiredPermission;
  const defaultRoute = getDefaultAllowedRoute();

  if (authLoading || (isAuthenticated && permissionsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>);

  }

  if (isAuthenticated && !requiredPermission && defaultRoute !== '/access-denied') {
    return <Navigate to={defaultRoute} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="h-10 w-10 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">{t("accessDenied", "Access Denied")}</h1>
        <p className="text-muted-foreground mb-6">{t("youDontHave", "You don't have permission to access this page.")}

          {requiredPermission &&
          <span className="block mt-2 text-sm">{t("required", "Required:")}
            <code className="bg-muted px-2 py-0.5 rounded">{
              Array.isArray(requiredPermission) ?
              requiredPermission.join(', ') :
              requiredPermission
              }</code>
            </span>
          }
        </p>
        <div className="flex gap-3 justify-center">
          <Link
            to={defaultRoute}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">{t("goToDashboard", "Go to Dashboard")}


          </Link>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors">{t("goBack", "Go Back")}


          </button>
        </div>
      </div>
    </div>);

};

/**
 * useCanAccess - Hook to check if user can access a route
 */
export const useCanAccess = (route) => {
  const { canAccessRoute, loading } = usePermissions();
  return { canAccess: canAccessRoute(route), loading };
};

/**
 * WithPermission - HOC to wrap components with permission check
 */
export const withPermission = (permission, FallbackComponent = null) => (WrappedComponent) => {
  return function PermissionWrapper(props) {
    return (
      <PermissionGate permission={permission} fallback={FallbackComponent ? <FallbackComponent {...props} /> : null}>
        <WrappedComponent {...props} />
      </PermissionGate>);

  };
};

/**
 * RoleGate - Show/hide based on user role
 */
export const RoleGate = ({
  roles = [],
  fallback = null,
  children
}) => {const { t } = useTranslation("common");
  const { isAdmin, isManager, isOperator } = usePermissions();
  const { user } = useAuth();

  const userRole = user?.role;

  let hasAccess = false;

  if (roles.length === 0) {
    hasAccess = true;
  } else if (roles.includes('admin') && isAdmin) {
    hasAccess = true;
  } else if (roles.includes('manager') && isManager) {
    hasAccess = true;
  } else if (roles.includes('operator') && isOperator) {
    hasAccess = true;
  } else if (roles.includes(userRole)) {
    hasAccess = true;
  }

  if (!hasAccess) {
    return fallback;
  }

  return <>{children}</>;
};

export default PermissionGate;
